import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import api from '@/lib/api';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export default function AuthGuard({ children, requiredRoles }: AuthGuardProps) {
  const { isAuthenticated, isInitialized, user, setAuth, clearAuth, setInitialized } = useAuthStore();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Try to restore session via refresh token
  useEffect(() => {
    if (!isInitialized) {
      api.post('/auth/refresh')
        .then((res) => {
          const { access_token, user: userData } = res.data.data;
          setAuth(access_token, userData);
        })
        .catch(() => {
          clearAuth();
          queryClient.clear();
        })
        .finally(() => {
          setInitialized();
        });
    }
  }, [isInitialized, setAuth, clearAuth, setInitialized, queryClient]);

  // Listen for multi-tab logout
  useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('hancomum-auth');
      bc.onmessage = (event) => {
        if (event.data?.type === 'LOGOUT') {
          clearAuth();
          queryClient.clear();
        }
      };
    } catch {
      // BroadcastChannel not supported
    }
    return () => bc?.close();
  }, [clearAuth, queryClient]);

  // Loading state
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/" state={{ from: location.pathname }} replace />;
  }

  // Pending user - can only access /dashboard with a pending message
  if (user.role === 'pending' && location.pathname !== '/dashboard') {
    return <Navigate to="/dashboard" replace />;
  }

  // Role-based access control
  if (requiredRoles && requiredRoles.length > 0) {
    if (!requiredRoles.includes(user.role)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}
